import { confirmPopup } from 'components/Popups/Confirm';
import { useCallback, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Trash2 } from 'lucide-react';
import { toast } from 'react-toastify';
import styled from 'styled-components';

import { PAGES } from 'constants/pages';

import { HOT_DRINKS_CATEGORY_ID, HOT_DRINKS_MACHINE_CATEGORY_ID } from 'pages/Products/constants/categories';

import { Header, HeaderTitle } from 'components/Header';
import { Loader } from 'components/Loader';
import { PageTitle } from 'components/PageTitle';
import { ActionsAndSave, type PageActionGroup } from 'components/PageHeader';
import { HotDrinksRecipeDetailsContent, RecipeFormData } from './HotDrinksRecipeDetailsContent';

import {
    ProductState,
    useGetDetailedProductQuery,
    useGetProductsWithRecipesQuery,
    useUpdateProductRecipeMutation,
    TotadminPermission,
} from 'data/__generated__';

export const HotDrinksRecipeDetails = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { hotDrinksRecipeId = '' } = useParams<{ hotDrinksRecipeId: string }>();
    const isCreating = location.pathname.includes('/create');
    const productId = isCreating ? null : hotDrinksRecipeId;

    const [formData, setFormData] = useState<RecipeFormData>({
        selectedProductId: null,
        ingredients: [],
    });

    const {
        loading: productLoading,
        data: productData,
        error: productError,
    } = useGetDetailedProductQuery({
        variables: { productId: productId! },
        skip: isCreating || !productId,
    });

    // Get all products with recipes to find available ingredient products
    const {
        loading: productsWithRecipesLoading,
        data: productsWithRecipesData,
        error: productsWithRecipesError,
    } = useGetProductsWithRecipesQuery();

    const [updateProductRecipe, { loading: updateLoading }] = useUpdateProductRecipeMutation();

    const isLoading = updateLoading;

    const handleFormChange = useCallback((data: RecipeFormData) => {
        setFormData(data);
    }, []);

    const product = productData?.detailedProduct || null;

    const hasChanges = isCreating
        ? formData.selectedProductId !== null && formData.ingredients.length > 0
        : !!product &&
          JSON.stringify(
              formData.ingredients.map((i) => ({
                  productId: i.productId,
                  quantity: i.quantity,
                  hotDrinksIngredientUnit: i.hotDrinksIngredientUnit,
              })),
          ) !==
              JSON.stringify(
                  (product.recipe?.ingredients || []).map((i) => ({
                      productId: i.productId,
                      quantity: i.quantity,
                      hotDrinksIngredientUnit: i.hotDrinksIngredientUnit,
                  })),
              );

    async function handleSubmit() {
        const targetProductId = isCreating ? formData.selectedProductId : productId;

        if (!targetProductId) {
            toast.error('Veuillez sélectionner un produit');
            return;
        }

        if (formData.ingredients.length === 0) {
            toast.error('Veuillez ajouter au moins un ingrédient');
            return;
        }

        if (formData.ingredients.some((ing) => ing.quantity <= 0)) {
            toast.error('Les quantités doivent être supérieures à 0');
            return;
        }

        try {
            const { data } = await updateProductRecipe({
                variables: {
                    productId: targetProductId,
                    recipe: {
                        ingredients: formData.ingredients.map((ing) => ({
                            productId: ing.productId,
                            quantity: ing.quantity,
                            hotDrinksIngredientUnit: ing.hotDrinksIngredientUnit,
                        })),
                    },
                },
            });

            if (data?.updateProductRecipe.success) {
                toast.success(isCreating ? 'Recette créée avec succès' : 'Recette mise à jour avec succès');
                if (isCreating) {
                    navigate('/hot-drinks-recipes');
                }
            } else {
                const error = data?.updateProductRecipe.error || 'Une erreur est survenue';
                toast.error(error);
            }
        } catch (error) {
            toast.error(`Erreur: ${error instanceof Error ? error.message : 'Une erreur est survenue'}`);
        }
    }

    async function handleArchive() {
        if (!productId) return;

        if (
            !(await confirmPopup({
                title: 'Supprimer la recette',
                message: 'Êtes-vous sûr de vouloir supprimer cette recette ?',
                confirmLabel: 'Supprimer',
                cancelLabel: 'Annuler',
            }))
        ) {
            return;
        }

        try {
            const { data } = await updateProductRecipe({
                variables: {
                    productId,
                    recipe: null,
                },
            });

            if (data?.updateProductRecipe.success) {
                toast.success('Recette supprimée avec succès');
                navigate('/hot-drinks-recipes');
            } else {
                const error = data?.updateProductRecipe.error || 'Une erreur est survenue';
                toast.error(error);
            }
        } catch (error) {
            toast.error(`Erreur: ${error instanceof Error ? error.message : 'Une erreur est survenue'}`);
        }
    }

    if ((productLoading && !isCreating) || productsWithRecipesLoading) {
        return (
            <Container>
                <Loader />
            </Container>
        );
    }

    if (!isCreating && productError) {
        throw new Error(`Erreur produit: ${productError.message}`);
    }
    if (productsWithRecipesError) {
        throw new Error(`Erreur produits: ${productsWithRecipesError.message}`);
    }
    if (!productsWithRecipesData) {
        throw new Error('Impossible de charger les produits');
    }

    const { getAllProducts: allProducts } = productsWithRecipesData;

    const availableIngredients = allProducts.filter(
        (p) => p.categoryId === HOT_DRINKS_CATEGORY_ID && p.state !== ProductState.Archived,
    );

    const productsWithoutRecipes = allProducts.filter(
        (p) => p.categoryId === HOT_DRINKS_MACHINE_CATEGORY_ID && !p.recipe && p.state !== ProductState.Archived,
    );

    const actionGroups: PageActionGroup[] = [
        {
            label: 'Recette',
            items: isCreating
                ? []
                : [
                      {
                          label: 'Supprimer la recette',
                          icon: <Trash2 />,
                          onClick: () => {
                              void handleArchive();
                          },
                          disabled: isLoading,
                          variant: 'destructive',
                          requiredPermissions: [TotadminPermission.ProductsManage],
                      },
                  ],
        },
        {
            label: 'Navigation',
            items: [{ label: 'Retour à la liste', icon: <ArrowLeft />, to: '/hot-drinks-recipes' }],
        },
    ];

    return (
        <Container>
            <Header>
                <HeaderTitle>
                    <PageTitle page={isCreating ? PAGES.hotDrinksRecipeCreate : PAGES.hotDrinksRecipeDetails} />
                </HeaderTitle>
                <ActionsAndSave
                    actionGroups={actionGroups}
                    onSave={handleSubmit}
                    saveDisabled={!hasChanges}
                    saveLoading={isLoading}
                    saveTitle={isCreating ? 'Créer la recette' : 'Mettre à jour'}
                    saveRequiredPermissions={[TotadminPermission.ProductsManage]}
                />
            </Header>
            <HotDrinksRecipeDetailsContent
                product={product}
                availableIngredients={availableIngredients}
                productsWithoutRecipes={productsWithoutRecipes}
                isCreating={isCreating}
                onFormChange={handleFormChange}
            />
        </Container>
    );
};

const Container = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
    height: 100%;
    background-color: ${({ theme }) => theme.backgroundColor};
`;
